import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Navigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { Link as RouterLink } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import { VersionLine } from "../components/VersionLine";
import { SUPPORT_EMAIL } from "../domain/contact";
import { SMS_LINKS, type SmsLinkSlug, optOutMailto, validIndianMobile } from "../domain/smsLinks";
import { flagEnabled } from "../config";

/**
 * The pages behind the fixed links in our DLT SMS templates
 * (https://ankhos.io/<slug>). STPL rejects a template whose link is a
 * variable, so every link is one of these twelve fixed addresses.
 *
 * 🔑 PUBLIC, outside RequireAuth. The person arriving has an SMS in hand and
 * may have no AnkhOS login at all (a hotspot subscriber never has one). The
 * page explains; an operator page offers a button into the console, and the
 * console's own RequireAuth does the sign-in.
 *
 * ⛔ Do not turn these into bare <Navigate>s. A redirect to a flag-gated screen
 * that is off on this site lands on the console home with no explanation of
 * why the SMS was sent.
 */
export function SmsLink({ slug }: { slug: SmsLinkSlug }) {
  const page = SMS_LINKS[slug];
  // 🔑 A person an operator INVITED has no login yet, so /signin must not jump
  // straight into sign-in: it shows the page with "Accept your invitation"
  // beside "Sign in" (owner 2026-10-18; the staff and sub-operator invite SMS
  // carry this fixed link).
  const invitations = slug === "signin" && flagEnabled("operator.logins");
  if (!invitations && page.opens && (!page.opens.flag || flagEnabled(page.opens.flag))) {
    return <OpenConsoleScreen slug={slug} to={page.opens.to} />;
  }
  return <SmsLinkPage slug={slug} />;
}

/**
 * The link stands for a real screen. Signed in: go straight there. Signed out:
 * show the page and its "Sign in to AnkhOS" button (RequireAuth signs in and
 * returns). It used to jump straight to sign-in, and a signed-out visitor saw
 * a blank page -- STPL's DLT review of ankhos.io/funds, 2026-10-21.
 */
function OpenConsoleScreen({ slug, to }: { slug: SmsLinkSlug; to: string }) {
  const auth = useAuth();
  if (auth.isAuthenticated) return <Navigate to={to} replace />;
  if (auth.isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 4 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Opening AnkhOS…</Typography>
      </Box>
    );
  }
  return <SmsLinkPage slug={slug} />;
}

function SmsLinkPage({ slug }: { slug: SmsLinkSlug }) {
  const page = SMS_LINKS[slug];
  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{page.title}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{page.intro}</Typography>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack component="ol" spacing={1} sx={{ m: 0, pl: 2.5 }}>
            {page.steps.map((s) => (
              <Typography component="li" variant="body2" key={s}>{s}</Typography>
            ))}
          </Stack>
        </Paper>
        {page.consoleTo && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 3 }}>
            <Button variant="contained" component={RouterLink} to={page.consoleTo}>
              {page.consoleLabel ?? "Sign in to AnkhOS"}
            </Button>
            {slug === "signin" && flagEnabled("operator.logins") && (
              <Button variant="outlined" component={RouterLink} to="/invite" data-testid="accept-invitation">
                First time? Accept your invitation
              </Button>
            )}
          </Stack>
        )}
        {slug === "optout" && <OptOutForm />}
        {page.note && <Alert severity="info" sx={{ mb: 3 }}>{page.note}</Alert>}
        <Typography variant="body2" color="text.secondary">
          Questions? Email <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Typography>
        <Box sx={{ mt: 4 }}><VersionLine /></Box>
      </Container>
    </Box>
  );
}

/**
 * 🚨 THERE IS NO OPT-OUT BACKEND YET. Like the Contact page, this composes an
 * email and never claims to have recorded anything. The TRAI 1909 codes on the
 * same page work today without us.
 */
function OptOutForm() {
  const [mobile, setMobile] = useState("");
  const ok = validIndianMobile(mobile);
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Ask us to stop</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField
          label="Mobile number"
          size="small"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          inputProps={{ inputMode: "numeric", maxLength: 10 }}
          error={mobile !== "" && !ok}
          helperText={mobile !== "" && !ok ? "10 digits, starting 6-9" : " "}
        />
        <Button variant="contained" disabled={!ok} href={ok ? optOutMailto(mobile) : undefined}>
          Email the request
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        This opens your email app with the request filled in. Send it to complete the request.
      </Typography>
    </Paper>
  );
}
